<!--
  The span of code a comment is on (logic/spanNote), compact: one line
  saying where, with its text colored by the file's language. A span that
  fits on that line shows whole; a longer one is folded to its start, and
  opens to every line of it. Shown above a span comment in its thread
  (CommentBody) and in the composer writing one (Composer), where `actions`
  add its controls. The code is rendered from the span's text (renderLine
  escapes it), never from Markdown.
-->
<script lang="ts">
  import type { Snippet } from 'svelte';
  import type { Span } from '../engine/types';
  import { renderLine } from '../logic/render';
  import { highlightText, languageOf } from '../diff/snippet';
  import { spanSummary, type SpanMarker } from '../logic/spanNote';

  interface Props {
    /** The file the span is in; its language colors the text. */
    path: string;
    span: SpanMarker;
    /** Controls after the summary (the composer's Remove quote). */
    actions?: Snippet;
  }
  let { path, span, actions }: Props = $props();

  const summary = $derived(spanSummary(span));
  const lines = $derived(span.quote.split('\n'));
  let open = $state(false);

  /** The text's colors, with the text they were computed for (null: plain). */
  let colors: { text: string; spans: Span[][] | null } = $state.raw({ text: '', spans: null });
  $effect(() => {
    const text = span.quote;
    let live = true;
    void languageOf(path)
      .then((lang) => highlightText(lang, text))
      .then(
        (spans) => {
          if (live) colors = { text, spans };
        },
        () => {},
      );
    return () => (live = false);
  });
  const spansOf = (i: number): Span[] => (colors.text === span.quote && colors.spans ? (colors.spans[i] ?? []) : []);
  /** The first line as shown folded: up to `summary.cut` characters of it. */
  const head = $derived(lines[0].slice(0, summary.cut));
  const headSpans = $derived(spansOf(0).flatMap((s) => (s.start >= summary.cut ? [] : [{ ...s, end: Math.min(s.end, summary.cut) }])));
</script>

<figure class="span-quote" class:open aria-label="Comment on {summary.where}">
  <div class="span-quote-head">
    {#if summary.folds}
      <button type="button" class="span-quote-toggle" aria-expanded={open} title={open ? 'Fold the quoted code' : 'Show all the quoted code'} onclick={() => (open = !open)}>
        <span class="caret" aria-hidden="true">{open ? '▾' : '▸'}</span>
        <span class="where">On</span>
        {#if !open}<code class="span-quote-line">{@html renderLine(head, headSpans)}{#if summary.more}<span class="more">…</span>{/if}</code>{/if}
        <span class="where">· {summary.where}</span>
      </button>
    {:else}
      <span class="where">On</span>
      <code class="span-quote-line">{@html renderLine(lines[0], spansOf(0))}</code>
      <span class="where">· {summary.where}</span>
    {/if}
    {#if actions}<span class="span-quote-actions">{@render actions()}</span>{/if}
  </div>
  {#if open}
    <pre class="span-quote-code"><code>{#each lines as l, i (i)}<span class="span-quote-row">{@html renderLine(l, spansOf(i))}</span>{'\n'}{/each}</code></pre>
  {/if}
</figure>

<style>
  .span-quote { margin: 0; display: grid; gap: 6px; padding: 4px 8px; border-left: 3px solid var(--accent); border-radius: 4px; background: var(--code); font-size: 12px; min-width: 0; }
  .span-quote-head { display: flex; align-items: center; gap: 6px; min-width: 0; }
  .span-quote-toggle { display: flex; align-items: center; gap: 6px; min-width: 0; flex: 1 1 auto; padding: 0; border: 0; background: none; color: inherit; font: inherit; text-align: left; cursor: pointer; }
  .span-quote-toggle:focus-visible { outline: 2px solid var(--accent); outline-offset: 2px; border-radius: 3px; }
  .caret { flex: none; width: 1em; color: var(--muted); font-size: 10px; }
  .where { flex: none; color: var(--muted); font: 11px ui-monospace, SFMono-Regular, monospace; }
  .span-quote-line { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: pre; font: 12px/1.5 ui-monospace, SFMono-Regular, monospace; color: var(--fg); background: none; padding: 0; }
  .more { color: var(--muted); }
  .span-quote-actions { margin-left: auto; flex: none; }
  .span-quote-code { margin: 0; max-height: 18em; overflow: auto; white-space: pre-wrap; overflow-wrap: anywhere; font: 12px/1.5 ui-monospace, SFMono-Regular, monospace; color: var(--fg); }
  .span-quote-code code { background: none; padding: 0; }
</style>
